/**
 * Saved views menu.
 *
 * Views are ALWAYS account-shared — there is no private option — so the save
 * dialog says so, and deletion goes behind a confirm dialog (a teammate can
 * remove a view someone else set up).
 *
 * Applying a view writes its filters into the URL rather than into local state.
 * That keeps one source of truth and makes views and shareable links the same
 * mechanism.
 */

import { useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Bookmark, ChevronDown, Info, Plus, Star, Trash2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { useSavedViews } from '@/hooks/useReports'
import { countActiveFilters, type ReportResource, type SavedView, type TableSort } from '@limo/shared'

interface Props {
  readonly resource: ReportResource
  readonly currentFilters: Readonly<Record<string, unknown>>
  readonly currentSort: TableSort<string>
  readonly currentColumns: readonly string[]
  readonly onApply: (view: SavedView) => void
}

export function SavedViewsMenu({
  resource,
  currentFilters,
  currentSort,
  currentColumns,
  onApply,
}: Props) {
  const { toast } = useToast()
  const { list, create, update, remove } = useSavedViews(resource)

  const [saveOpen, setSaveOpen] = useState(false)
  const [name, setName] = useState('')
  const [pendingDelete, setPendingDelete] = useState<SavedView | null>(null)

  const views = list.data ?? []
  const activeCount = countActiveFilters(currentFilters as never)

  const handleSave = async (): Promise<void> => {
    const trimmed = name.trim()
    if (trimmed.length === 0) return

    try {
      await create.mutateAsync({
        name: trimmed,
        filters: currentFilters,
        sort: currentSort,
        columns: currentColumns,
      })
      toast({ title: 'View saved', description: `"${trimmed}" is available to your whole team.` })
      setName('')
      setSaveOpen(false)
    } catch (error) {
      // Includes the friendly duplicate-name message from useSavedViews.
      toast({
        title: 'Could not save view',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  const handleOverwrite = async (view: SavedView): Promise<void> => {
    try {
      await update.mutateAsync({
        id: view.id,
        filters: currentFilters,
        sort: currentSort,
        columns: currentColumns,
      })
      toast({ title: 'View updated', description: `"${view.name}" now matches the current view.` })
    } catch (error) {
      toast({
        title: 'Could not update view',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  const handleDelete = async (view: SavedView): Promise<void> => {
    try {
      await remove.mutateAsync(view.id)
      toast({ title: 'View deleted', description: `"${view.name}" was removed for everyone.` })
    } catch (error) {
      toast({
        title: 'Could not delete view',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    } finally {
      setPendingDelete(null)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Bookmark className="h-3.5 w-3.5" />
            <span>Views</span>
            {views.length > 0 && (
              <span className="text-xs text-muted-foreground">({views.length})</span>
            )}
            <ChevronDown className="h-3.5 w-3.5 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel>Saved views</DropdownMenuLabel>
          <DropdownMenuSeparator />

          {list.isLoading ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">Loading…</p>
          ) : views.length === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">
              No saved views yet. Filter the table, then save it for your team.
            </p>
          ) : (
            views.map((view) => (
              <div key={view.id} className="flex items-center gap-1 px-1">
                <DropdownMenuItem
                  className="flex-1 cursor-pointer"
                  onClick={() => onApply(view)}
                >
                  <Star className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                  <span className="truncate">{view.name}</span>
                </DropdownMenuItem>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  title="Overwrite with the current filters"
                  onClick={(e) => {
                    e.preventDefault()
                    void handleOverwrite(view)
                  }}
                >
                  <Bookmark className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 text-destructive"
                  title="Delete this view"
                  onClick={(e) => {
                    e.preventDefault()
                    setPendingDelete(view)
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem className="cursor-pointer" onClick={() => setSaveOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Save current view…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
            <DialogDescription>
              Saves {activeCount} active filter{activeCount === 1 ? '' : 's'}, the sort order, and{' '}
              {currentColumns.length} selected column{currentColumns.length === 1 ? '' : 's'}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Input
              autoFocus
              placeholder="e.g. Unpaid this month"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSave()
              }}
            />
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Saved views are shared with everyone on this account.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSave()}
              disabled={name.trim().length === 0 || create.isPending}
            >
              {create.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — views are shared, so this affects teammates. */}
      <AlertDialog open={!!pendingDelete} onOpenChange={() => setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{pendingDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This view is shared with your whole account, so it will be removed for everyone. Your
              bookings and quotes are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep view</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDelete && void handleDelete(pendingDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete view
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
